import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Header } from "../../components/Header";
import googleLogo from '../../../../assets/login/google.jpg'

// API imports
import authApi from "../../../../api/authApi"
import userMetadataApi from "../../../../api/userMetadataApi";
import { LoginValues } from "../../../../api/types"
import { consumePostLoginRedirect, persistSessionValue } from "../../../../utils/authStorage";
import { getUserFriendlyErrorMessage } from "../../../../utils/errorMessages";

function Login() {
    const navigate = useNavigate();
    const { register, handleSubmit, formState } = useForm<LoginValues>();
    const { errors, isSubmitting } = formState;
    const [loginError, setLoginError] = useState<string | null>(null);
    const [showPassword, setShowPassword] = useState(false)

    const redirectAfterLogin = async (roleFromLogin?: string) => {
        consumePostLoginRedirect();

        let nextRole = roleFromLogin;
        if (!nextRole) {
            try {
                const metadata = await userMetadataApi.getUserMetadata();
                nextRole = metadata?.role;
                if (nextRole) {
                    persistSessionValue('user_role', nextRole);
                } else {
                    sessionStorage.removeItem('user_role');
                    localStorage.removeItem('user_role');
                }
            } catch (error) {
                sessionStorage.removeItem('user_role');
                localStorage.removeItem('user_role');
            }
        }

        window.location.replace(nextRole === "admin" ? "/admin" : "/home");
    };

    const onSubmit = async (data: LoginValues) => {
        try {
            setLoginError(null);

            // SUCCESS: Redirect to home
            const user = await authApi.login(data.email, data.password, data.phone);
            persistSessionValue('just_logged_in', 'true');
            sessionStorage.removeItem('login_cashflow_refresh_started');
            await redirectAfterLogin(user.role);

        } catch (error: any) {
            const rawErrorMessage = error?.response?.data?.detail || error?.response?.data?.message || error.message || "";
            const errorMessage = getUserFriendlyErrorMessage(error, "Login failed. Please try again.");
            console.error("Login error:", error);

            // Login with Unverified Email
            if (String(rawErrorMessage).toLowerCase().includes("email not verified")) {
                sessionStorage.setItem("email", data.email)

                // Send Email OTP
                await authApi.sendEmailOtp(data.email);
                navigate("/emailverification")
            } else {
                setLoginError(errorMessage)
            }

            // Login with Unverified Phone
            if (String(rawErrorMessage).toLowerCase().includes("phone number not verified")) {
                navigate("/firebase-mobile")
            } else {
                setLoginError(errorMessage)
            }
        }
    };

    const handleGoogleLogin = async () => {
        try {
            setLoginError(null);
            await authApi.googleSignIn();
            persistSessionValue('just_logged_in', 'true');
            sessionStorage.removeItem('login_cashflow_refresh_started');
            await redirectAfterLogin();
        } catch (error: any) {
            const msg = getUserFriendlyErrorMessage(error, "Authentication failed. Please try again.");
            setLoginError(msg);
            console.error("Google login failed:", error);
        }
    };

    const onSubmitTest = async (data: Record<string, any>) => {
        const mockToken = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6Ik1vY2sgVXNlciIsImlhdCI6MTUxNjIzOTAyMiwiZXhwIjoyNTM0MDIzMDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c";
        persistSessionValue("token", mockToken);
        persistSessionValue('just_logged_in', 'true');
        sessionStorage.removeItem('login_cashflow_refresh_started');
        await redirectAfterLogin();
    };

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            <Header />
            <div className="flex flex-col items-center flex-1 bg-gray-50 p-5">
                <div className="w-full max-w-[573px] bg-white rounded-xl shadow-sm p-8 flex flex-col gap-6">
                    <h1 className="w-full font-sans font-semibold text-2xl leading-[38px] text-center text-gray-800">
                        Welcome back!
                    </h1>

                    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-14">
                        <div className="flex flex-col gap-4">
                            {/* Email */}
                            <div className="w-full">
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                    Email
                                </label>
                                <input
                                    id="email"
                                    type="email"
                                    {...register("email", { required: "Email is required" })}
                                    autoComplete="email"
                                    className="w-full h-12 px-4 border rounded-md bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            {/* Phone */}
                            {/* <div className="w-full">
                                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                                    Phone
                                </label>
                                <input
                                    id="phone"
                                    type="phone"
                                    {...register("phone", { required: "Phone is required" })}
                                    className="w-full h-12 px-4 border rounded-md bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div> */}

                            {/* Password Field */}
                            <div className="flex flex-col gap-1">
                                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                    Password
                                </label>
                                <div className="relative">
                                    <input
                                        id="password"
                                        type={showPassword ? "text" : "password"}
                                        {...register("password", { required: "Password is required" })}
                                        autoComplete="current-password"
                                        className="w-full h-12 px-4 pr-12 border rounded-md bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter your password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                    >
                                        {!showPassword ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                                            </svg>
                                        ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                            </div>

                            <div className="w-full">
                                <span
                                    className="text-[16px] font-semibold text-gray-800 cursor-pointer underline"
                                    onClick={() => navigate("/forgot-password")}
                                >
                                    Forgot Password?
                                </span>
                            </div>

                            {loginError && (
                                <div className="w-full text-sm text-red-600">
                                    {loginError}
                                </div>
                            )}
                        </div>

                        <div className="flex flex-col gap-6">
                            {/* Login Button */}
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="button-1 w-full bg-gray-800 hover:bg-gray-700 disabled:!bg-gray-400 disabled:!cursor-not-allowed"
                            >
                                {isSubmitting ? "Logging in..." : "Login"}
                            </button>

                            <div className="w-full flex items-center">
                                <div className="flex-1 h-px bg-gray-300"></div>
                                <span className="mx-4 text-[16px] text-gray-800 font-medium">or</span>
                                <div className="flex-1 h-px bg-gray-300"></div>
                            </div>

                            <div className="w-full flex flex-col gap-6">
                                {/* Google Login */}
                                <button
                                    type="button"
                                    onClick={() => handleGoogleLogin()}
                                    className="button-1 !bg-white !text-gray-800 w-full border border-gray-800 flex items-center justify-center gap-2"
                                >
                                    <img src={googleLogo} alt="Google" className="w-6 h-6" />
                                    Continue with Google
                                </button>

                                {/* Signup Redirect */}
                                <div className="w-full text-center">
                                    <span className="text-[16px] text-[#6B7280]">Haven't got an account? </span>
                                    <span
                                        className="underline text-[16px] text-gray-800 font-medium cursor-pointer"
                                        onClick={() => navigate("/signup")}
                                    >
                                        Sign up
                                    </span>
                                </div>
                            </div>

                        </div>

                    </form>
                </div>
            </div>
        </div>
    );
}

export default Login;
